import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, X, Calendar as CalendarIcon, MapPin, Users, Award, Check, UserPlus, Clock, Building2 } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, parseISO, isBefore, startOfDay } from 'date-fns';
import { toast } from 'sonner';
import { AuthContext, API } from '../App';
import { formatTimeRange } from '../utils/dateTime';

const CalendarView = ({ opportunities, basePath = '/opportunity', onRefresh }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [signingUp, setSigningUp] = useState(false);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const firstDayOfWeek = monthStart.getDay();
  const emptyCells = Array(firstDayOfWeek).fill(null);

  const getOpportunitiesForDate = (date) => {
    return opportunities.filter(opp => {
      const oppDate = parseISO(opp.date);
      return isSameDay(oppDate, date);
    });
  };

  const goToPreviousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const goToNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const closePopup = () => { setSelectedOpp(null); setSigningUp(false); };

  // Close popup on Escape key
  useEffect(() => {
    if (!selectedOpp) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closePopup(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedOpp]);

  const handleQuickSignup = async () => {
    if (!selectedOpp) return;
    setSigningUp(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/signups/${selectedOpp.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Sign-up request sent! You'll be notified once approved.");
      closePopup();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sign up');
    } finally {
      setSigningUp(false);
    }
  };

  // Compute sign-up state for the popup based on the latest opportunity data.
  const popupState = (() => {
    if (!selectedOpp) return null;
    const isUnlimited = !selectedOpp.capacity || selectedOpp.capacity === 0;
    const isFull = !isUnlimited && selectedOpp.signups_count >= selectedOpp.capacity;
    const spotsLeft = isUnlimited ? null : selectedOpp.capacity - selectedOpp.signups_count;
    const oppDate = parseISO(selectedOpp.date);
    const isPast = isBefore(oppDate, startOfDay(new Date()));
    const isClosed = selectedOpp.status === 'closed';
    const isSignedUp = !!selectedOpp.user_signed_up;
    const isVolunteer = user?.role === 'user';
    const canSignUp = isVolunteer && !isSignedUp && !isFull && !isPast && !isClosed;
    return { isUnlimited, isFull, spotsLeft, isPast, isClosed, isSignedUp, isVolunteer, canSignUp };
  })();

  return (
    <div className="card" data-testid="calendar-view">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-base md:text-lg font-bold text-[#222222]">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        <div className="flex gap-2">
          <button onClick={goToPreviousMonth} className="p-2 hover:bg-[#FBF4EA] rounded-lg transition-colors" data-testid="prev-month-button">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={goToNextMonth} className="p-2 hover:bg-[#FBF4EA] rounded-lg transition-colors" data-testid="next-month-button">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center text-sm font-semibold text-[#666666] p-2">{day}</div>
        ))}

        {emptyCells.map((_, index) => <div key={`empty-${index}`} className="aspect-square" />)}

        {monthDays.map((day) => {
          const dayOpportunities = getOpportunitiesForDate(day);
          const hasOpportunities = dayOpportunities.length > 0;
          const isToday = isSameDay(day, new Date());

          return (
            <div
              key={format(day, 'yyyy-MM-dd')}
              className={`aspect-square p-2 border-2 rounded-lg transition-all ${
                isToday ? 'border-[#C9A227] bg-[#FFFBF0]' : 'border-[#E9DDCF]'
              } ${hasOpportunities ? 'hover:border-[#8B2E3C] cursor-pointer' : ''}`}
              data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
            >
              <div className="text-sm font-medium text-[#222222] mb-1">{format(day, 'd')}</div>
              {hasOpportunities && (
                <div className="space-y-1">
                  {dayOpportunities.slice(0, 2).map((opp) => (
                    <button
                      type="button"
                      key={opp.id}
                      onClick={() => setSelectedOpp(opp)}
                      className="block w-full text-left text-xs bg-[#8B2E3C] text-white px-1.5 py-0.5 rounded truncate hover:bg-[#6d2330] transition-colors"
                      title={`${opp.title} — ${opp.partner_org_name || ''}`}
                      data-testid={`calendar-opp-${opp.id}`}
                    >
                      {opp.title}
                      {opp.partner_org_name && (
                        <span className="block text-[10px] text-[#E9DDCF] truncate" data-testid={`calendar-opp-org-${opp.id}`}>
                          {opp.partner_org_name}
                        </span>
                      )}
                    </button>
                  ))}
                  {dayOpportunities.length > 2 && (
                    <button
                      type="button"
                      onClick={() => setSelectedOpp(dayOpportunities[2])}
                      className="text-xs text-[#8B2E3C] font-medium px-2 hover:underline"
                    >
                      +{dayOpportunities.length - 2} more
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Quick-view / Quick-sign-up popup */}
      {selectedOpp && popupState && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={closePopup} data-testid="calendar-quick-signup-modal">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-[#E9DDCF] flex items-start justify-between sticky top-0 bg-white z-10">
              <div className="flex-1 min-w-0 pr-3">
                <h2 className="text-lg font-bold text-[#222] truncate">{selectedOpp.title}</h2>
                {selectedOpp.partner_org_name && (
                  <p className="text-xs text-[#8B2E3C] font-medium flex items-center gap-1 mt-0.5">
                    <Building2 className="w-3 h-3" /> {selectedOpp.partner_org_name}
                  </p>
                )}
              </div>
              <button onClick={closePopup} className="text-[#666] hover:text-[#222]" data-testid="calendar-popup-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-2 text-sm text-[#444]">
              <div className="flex items-start gap-2">
                <CalendarIcon className="w-4 h-4 text-[#8B2E3C] mt-0.5 shrink-0" />
                <span>{format(parseISO(selectedOpp.date), 'EEEE, MMM d, yyyy')}{selectedOpp.start_time ? ` · ${formatTimeRange(selectedOpp.start_time, selectedOpp.end_time)}` : ''}</span>
              </div>
              {selectedOpp.location && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-[#8B2E3C] mt-0.5 shrink-0" />
                  <span>{selectedOpp.location}</span>
                </div>
              )}
              <div className="flex items-start gap-2">
                <Users className="w-4 h-4 text-[#8B2E3C] mt-0.5 shrink-0" />
                <span>
                  {popupState.isUnlimited
                    ? `${selectedOpp.signups_count} signed up (unlimited spots)`
                    : `${selectedOpp.signups_count} / ${selectedOpp.capacity}${popupState.isFull ? ' — Full' : popupState.spotsLeft <= 3 && popupState.spotsLeft > 0 ? ` (${popupState.spotsLeft} left)` : ''}`}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <Award className="w-4 h-4 text-[#8B2E3C] mt-0.5 shrink-0" />
                <span>{selectedOpp.credit_hours} hours credit</span>
              </div>
              {popupState.isPast && (
                <div className="flex items-center gap-2 text-[#888] text-xs mt-2">
                  <Clock className="w-3.5 h-3.5" /> This opportunity has already passed.
                </div>
              )}
              {selectedOpp.description && (
                <p className="text-sm text-[#444] mt-3 whitespace-pre-wrap">{selectedOpp.description}</p>
              )}
            </div>

            <div className="p-5 border-t border-[#E9DDCF] flex flex-wrap justify-end gap-2 sticky bottom-0 bg-white">
              <button
                onClick={() => { const id = selectedOpp.id; closePopup(); navigate(`${basePath}/${id}`); }}
                className="btn-outline text-sm"
                data-testid="calendar-popup-view-details-button"
              >
                View Full Details
              </button>
              {popupState.isVolunteer && (
                popupState.isSignedUp ? (
                  <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#2D6A4F] text-white" data-testid="calendar-popup-signed-up-badge">
                    <Check className="w-4 h-4" /> Signed Up
                  </span>
                ) : (
                  <button
                    onClick={handleQuickSignup}
                    disabled={!popupState.canSignUp || signingUp}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      popupState.canSignUp ? 'bg-[#8B2E3C] text-white hover:bg-[#6d2330] active:scale-95' : 'bg-[#E9DDCF] text-[#999] cursor-not-allowed'
                    }`}
                    data-testid="calendar-popup-quick-signup-button"
                  >
                    <UserPlus className="w-4 h-4" />
                    {signingUp ? 'Signing up…' : popupState.isFull ? 'Full' : popupState.isPast ? 'Past' : popupState.isClosed ? 'Closed' : 'Quick Sign Up'}
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
